import { defaultBrand } from '@aj/tokens';

import { useT } from '../providers/I18nProvider.js';

export type WordmarkProps = {
  /** Rendered height in px; width follows the asset's 2.6:1 ratio. */
  height?: number;
  className?: string;
};

/**
 * The wordmark, as two images with CSS choosing between them.
 *
 * The mark is flat ink on transparency, so it needs one file per ground. An
 * `<img>` cannot inherit `currentColor` the way an inlined `<svg>` can, and
 * inlining would close the white-label seam in @aj/tokens, where a partner's
 * logo is a URL rather than a bundled component.
 *
 * Which file shows is a CSS decision rather than a React one, and deliberately:
 * theme is three-state — `light`, `dark`, or absent meaning "follow the
 * system" — so resolving it in JS would mean a matchMedia listener, a render
 * pass, and a visible swap on first paint. The stylesheet already knows the
 * answer before React mounts. See `.aj-wordmark` in app.css, which mirrors the
 * exact selector order the colour tokens use.
 *
 * Both files are precached, so the hidden one costs a request that already
 * happened at install.
 */
export function Wordmark({ height = 30, className }: WordmarkProps) {
  const t = useT();
  const name = t('app.name');

  return (
    <>
      <img
        src={defaultBrand.logo.onLight}
        alt={name}
        height={height}
        style={{ height }}
        className={`aj-wordmark aj-wordmark--on-light w-auto ${className ?? ''}`}
      />
      <img
        src={defaultBrand.logo.onDark}
        // Both carry the name rather than one being aria-hidden: the hidden
        // half is `display: none`, which removes it from the accessibility
        // tree, so exactly one name is ever exposed. Hiding this one instead
        // would leave dark mode with an unnamed logo.
        alt={name}
        height={height}
        style={{ height }}
        className={`aj-wordmark aj-wordmark--on-dark w-auto ${className ?? ''}`}
      />
    </>
  );
}
